import React, { useMemo, useRef } from "react";
import type { FC } from "react";

import cn from "classnames";

import { useMask } from "./hooks/useMask";
import { renderRowWithSlots } from "./utils/renderRowWithSlots";
import { UNIBOX_MAIN_DISPLAY_NAME, UNIBOX_MAIN_TEST_ID } from "./vars";
import { Gap } from "../../../helpers/common/Gap/Gap";
import { getChildrenRecord } from "../../../utils/react";

import type { UniBoxMainProps } from "./types";

import classes from "./UniBoxMain.styl";

const calcSlots = (slots: unknown[]): number => {
    return slots.filter((slot) => !!slot).length;
};

/**
 * Контейнер, который может принимать в себя множество дочерних компонентов и формировать из них различные композиции поверх других элементов.
 *
 * Служит конструктором, в который можно положить как готовые предустановленные компоненты, так и локальные, не имеющие готовой интерпретации в ДС.
 *
 * Систематизирует динамические отступы и оффсеты вложенных элементов.
 *
 * Элементы UniBoxMain находятся в 9 слотах, не перекрывают друг друга, не выходят за пределы слота и контейнера.
 *
 * **Важно:** для корректной работы UniBoxMain, родительскому компоненту необходимо задать position, отличный от static.
 *
 * <a href="https://www.figma.com/design/64y1aDfl0lSSpek3sq1Lrt/%F0%9F%8D%AC-Cards-%26-Cells-%26-Views?m=auto&node-id=6181-53153&t=CGbKD6mrS1ryh4Rw-1" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<UniBoxMain.SlotTopLeft />` | Слот, помещающий элемент в верхнюю левую часть контейнера. |
 * | `<UniBoxMain.SlotTop />` | Слот, помещающий элемент в верхнюю часть контейнера. |
 * | `<UniBoxMain.SlotTopRight />` | Слот, помещающий элемент в верхнюю правую часть контейнера. |
 * | `<UniBoxMain.SlotLeft />` | Слот, помещающий элемент в левую часть контейнера. |
 * | `<UniBoxMain.SlotMiddle />` | Слот, помещающий элемент в центральную часть контейнера. |
 * | `<UniBoxMain.SlotRight />` | Слот, помещающий элемент в правую часть контейнера. |
 * | `<UniBoxMain.SlotBottomLeft />` | Слот, помещающий элемент в нижнюю левую часть контейнера. |
 * | `<UniBoxMain.SlotBottom />` | Слот, помещающий элемент в нижнюю часть контейнера. |
 * | `<UniBoxMain.SlotBottomRight />` | Слот, помещающий элемент в нижнюю правую часть контейнера. |
 */

export const UniBoxMain: FC<UniBoxMainProps> = ({
    "data-tsid": tsid = UNIBOX_MAIN_TEST_ID,
    width,
    height,
    customVarsStyles,
    children,
    mask,
}) => {
    const uniBoxMainRef = useRef<HTMLDivElement | null>(null);

    const { svgMaskElement, svgMaskStyle } = useMask({
        mask,
        containerRef: uniBoxMainRef,
    });

    const uniboxMainClassnames = cn(classes["uni-box-main"], customVarsStyles);

    const {
        SlotTopLeft,
        SlotTop,
        SlotTopRight,
        SlotLeft,
        SlotMiddle,
        SlotRight,
        SlotBottomLeft,
        SlotBottom,
        SlotBottomRight,
    } = useMemo(
        () =>
            getChildrenRecord(children, UNIBOX_MAIN_DISPLAY_NAME, {
                SlotTopLeft: {},
                SlotTop: {},
                SlotTopRight: {},
                SlotLeft: {},
                SlotMiddle: {},
                SlotRight: {},
                SlotBottomLeft: {},
                SlotBottom: {},
                SlotBottomRight: {},
                Unknown: {
                    skipElement: true,
                },
            }),
        [children],
    );

    const topRowSlots = [SlotTopLeft, SlotTop, SlotTopRight];
    const middleRowSlots = [SlotLeft, SlotMiddle, SlotRight];
    const bottomRowSlots = [SlotBottomLeft, SlotBottom, SlotBottomRight];

    const topRowSlotsCount = calcSlots(topRowSlots);
    const middleRowSlotsCount = calcSlots(middleRowSlots);
    const bottomRowSlotsCount = calcSlots(bottomRowSlots);

    const hasTopRow = topRowSlotsCount > 0;
    const hasMiddleRow = middleRowSlotsCount > 0;
    const hasBottomRow = bottomRowSlotsCount > 0;

    const activeRowCount = [hasTopRow, hasMiddleRow, hasBottomRow].filter(
        (row) => row,
    ).length;

    const uniBoxStyles = {
        "--uni-box-main-width": width,
        "--uni-box-main-height": height,
        "--uni-box-row-count": activeRowCount,
        "--uni-box-top-row-slots": topRowSlotsCount,
        "--uni-box-middle-row-slots": middleRowSlotsCount,
        "--uni-box-bottom-row-slots": bottomRowSlotsCount,
        ...svgMaskStyle,
    };

    return (
        <>
            {svgMaskElement}

            <Gap
                style={uniBoxStyles}
                className={uniboxMainClassnames}
                data-tsid={tsid}
                data-uikit={UNIBOX_MAIN_DISPLAY_NAME}
                wrapperRef={uniBoxMainRef}
                contentClassName={cn(
                    classes["uni-box-main-gap-container"],
                    classes["uni-box-main-row-gap-values"],
                )}
            >
                {renderRowWithSlots({
                    type: "top-row",
                    slots: topRowSlots,
                })}
                {renderRowWithSlots({
                    type: "middle-row",
                    slots: middleRowSlots,
                })}
                {renderRowWithSlots({
                    type: "bottom-row",
                    slots: bottomRowSlots,
                })}
            </Gap>
        </>
    );
};

UniBoxMain.displayName = UNIBOX_MAIN_DISPLAY_NAME;

export default UniBoxMain;
